const { randomUUID, transformSchema } = require('@/utils');
const { isArray, isString, isEmpty, isNumber } = require('@/utils/is');
const { getDate } = require('@/views/fusion/investment/intelligence-create/tools');
const { CHANNEL_INTERFACE_TYPE } = require('@/enums');
const { getChannelsWithEditableCode } = require('@/tools');

/**
 * 获取唯一键配置
 * @typedef {Object} UniqueOptions
 * @property {number} row_index 行索引
 * @property {number} column_index 列索引
 * @property {string} property 列属性名
 * @property {string} custom_key 自定义键名
 */

/**
 * 获取唯一键
 * @param {UniqueOptions} config 配置
 * @returns {string}
 */
const getUniqueKey = ({ row_index, column_index, property, custom_key }) => {
  const keys = [row_index, column_index, property, custom_key];
  return keys.join('_');
};

/**
 * 获取Popover组件的ref名
 * @param {UniqueOptions} config 配置
 * @returns {string}
 */
const getPopoverRefKey = (config) => {
  return getUniqueKey({
    ...config,
    custom_key: `${randomUUID()}_table_el_popover_ref`,
  });
};

/**
 * 关闭Popover
 * @param {Object} s 组件实例对象
 * @param {string} key ref键名
 * @returns {void}
 */
const closePopover = (s, key) => {
  s.$refs[key].doClose?.();
  s.refreshTable(); // 手动刷新表格
};

/**
 * 显示Popover
 * @param {Object} s 组件实例对象
 * @param {string} key ref键名
 * @returns {void}
 */
const showPopover = (s, key) => {
  s.$refs[key].doShow?.();
};

// 优化目标枚举key
const convert_goal_enum_val_key = 'convert_goal_enum_val_key';

module.exports = (s) => {
  return [
    {
      name: '广告主账户',
      align: 'center',
      width: 200,
      fixed: 'left',
      format: (row) => {
        const h = s.$createElement;
        const { advertiser_id } = row;
        const { advertiser_ids } = s.optionsEnumsVal;
        const ele = h(
          'div',
          {
            class: 'w-full truncate',
          },
          advertiser_ids[advertiser_id] || '-'
        );
        return ele;
      },
    },
    {
      name: '广告名称',
      align: 'center',
      width: 200,
      fixed: 'left',
      value: 'campaign_name',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { property } = column;
        const { campaign } = row;
        const { campaign_name } = campaign;
        const form_context = s.local_sharing_data.getFormContext();
        const { channel_config } = form_context.config;
        const { campaign_name_separator } = channel_config.campaign;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = !s.isAddCreative;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              campaign_name
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                is_render_edit && h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        const pre_name = campaign_name.split(campaign_name_separator)[0];
                        s.form_data_config.campaign_name.campaign_name.value = pre_name;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.campaign_name,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        const name = campaign_name.split(campaign_name_separator).with(0, data.campaign_name).join(campaign_name_separator);
                        campaign.campaign_name = name;
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '渠道号',
      align: 'center',
      width: 200,
      value: 'channel_alias',
      format: (row, column, _, row_index) => {
        /** 是否支持编辑渠道号 */
        const is_render_edit = getChannelsWithEditableCode().includes(s.init_ops.channel_interface) && !s.isAddCreative;
        const h = s.$createElement;
        const { property } = column;
        const { campaign } = row;
        const { channel_alias, campaign_name } = campaign;
        const form_context = s.local_sharing_data.getFormContext();
        const { channel_config } = form_context.config;
        const { campaign_name_separator } = channel_config.campaign;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              channel_alias
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                is_render_edit && h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        s.form_data_config.channel_code.channel_code.value = channel_alias;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.channel_code,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        s.useGetChannelDetails([data.channel_code]).then(
                          (res) => {
                            const [channel_code] = res;
                            const { alias, config: { platform }, game_id } = channel_code;
                            const full_names = campaign_name.split(campaign_name_separator);
                            const pre_name = full_names[0];
                            const last_name = full_names[full_names.length - 1];
                            const assigned_res = [
                              pre_name,
                              alias,
                              '',
                              platform,
                              game_id,
                              last_name,
                            ].join(campaign_name_separator);
                            campaign.campaign_name = assigned_res;
                            campaign.channel_alias = alias;
                            closePopover(s, popover_ref_key);
                          }
                        );
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '投放平台',
      align: 'center',
      width: 150,
      value: 'delivery_platform_type',
      format: (row, column) => {
        const { campaign: { delivery_platform_type }} = row;
        const { property } = column;
        return s.optionsEnumsVal[property]?.[delivery_platform_type] || '-';
      },
    },
    {
      name: '投放内容',
      align: 'center',
      width: 200,
      value: 'delivery_content_id',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { campaign, campaign: { delivery_content_name, delivery_content_id }} = row;
        const is_render_edit = s.supportEditDeliveryContent();
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property: column.property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const ele = h('div', { class: 'flex justify-center items-center' }, [
          h(
            'div',
            {
              class: 'ml-10 mr-10 min-w-0 truncate inline-block',
            },
            delivery_content_name || '-'
          ),
          h(
            'el-popover',
            {
              ref: popover_ref_key,
              props: {
                trigger: 'manual',
              },
            },
            [
              is_render_edit && h(
                'i',
                {
                  slot: 'reference',
                  class: 'el-icon-edit-outline font-16',
                  on: {
                    async click() {
                      s.closeAllTablePopover();
                      showPopover(s, popover_ref_key);
                      s.form_data_config.delivery_content.delivery_content_id.value = `${delivery_content_id}#${delivery_content_name}`;
                      s.form_data_config.delivery_content.delivery_content_id.options = await s.getDeliveryContent(row);
                    },
                  },
                },
                null
              ),
              h(
                'app-form',
                {
                  props: {
                    formData: s.form_data_config.delivery_content,
                  },
                  on: {
                    cancel() {
                      closePopover(s, popover_ref_key);
                    },
                    confirm(data) {
                      const delivery_content = data.delivery_content_id.split('#');
                      campaign.delivery_content_id = delivery_content[0];
                      campaign.delivery_content_name = delivery_content[1];
                      closePopover(s, popover_ref_key);
                    },
                  },
                },
                null
              ),
            ]
          ),
        ]);
        return ele;
      },
    },
    {
      name: '应用渠道包',
      align: 'center',
      width: 200,
      value: 'channel_package_id',
      format: (row, column) => {
        const { property } = column;
        const h = s.$createElement;
        const { campaign } = row;
        const { channel_package_id, channel_package_name, raw_channel_package_id } = campaign;
        const hasOwnProperty = (target, key) => Object.prototype.hasOwnProperty.call(target, key);

        /** 保存原始的渠道包ID */
        if (!hasOwnProperty(campaign, 'channel_package_id')) {
          campaign.raw_channel_package_id = channel_package_id;
        }

        /** 保存原始的渠道包名称 */
        if (!hasOwnProperty(campaign, 'channel_package_name')) {
          campaign.raw_channel_package_name = channel_package_name;
        }

        /** 支持编辑应用渠道包的媒体平台 */
        const is_support_channel = s.supportEditChannelPackage(row);

        /**
          * 判断原始的渠道包ID是否有数据, 规则如下：
          * 1. 渠道包ID为字符串且不为空
          * 2. 渠道包ID为数字且不为0
          */
        const has_channel_package_id = (isString(raw_channel_package_id) && !isEmpty(raw_channel_package_id)) || (isNumber(raw_channel_package_id) && raw_channel_package_id !== 0);

        /** 支持的媒体平台且不存在应用渠道包才渲染编辑按钮 */
        const is_render_edit = is_support_channel && !has_channel_package_id && !s.isAddCreative;

        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              channel_package_id ? `${channel_package_name}（${channel_package_id}）` : '-'
            ),
            is_render_edit && h(
              'i',
              {
                slot: 'reference',
                class: 'el-icon-edit-outline font-16',
                on: {
                  click() {
                    s.onDataDialog({
                      title: '应用渠道包',
                      form_key: property,
                      payload: {
                        row,
                      },
                    });
                  },
                },
              },
              null
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '广告版位',
      align: 'center',
      width: 200,
      value: 'site_sets',
      format: (row, column) => {
        const { property } = column;
        const { campaign: { site_set_type, site_sets = [] }} = row;
        /** 是否自动版位 */
        const is_auto_site = site_set_type === 1;
        const site_sets_value = site_sets.includes('SITE_SET_WECHAT') ? [...site_sets.filter(v => v !== 'SITE_SET_WECHAT' && v !== 'SITE_SET_WECHAT_PLUGIN'), 'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN'] : site_sets;
        return is_auto_site ? '自动版位' : site_sets_value.map((val) => s.optionsEnumsVal[property]?.[val] || '').join('、');
      },
    },
    {
      name: '广告排期',
      align: 'center',
      width: 200,
      value: 'schedule_type',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { campaign } = row;
        const { schedule_type, begin_date, end_date } = campaign;
        const { property } = column;
        /** 是否长期投放 */
        const isNow = (val) => val === 'SCHEDULE_FROM_NOW';
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = !s.isAddCreative;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              isNow(schedule_type) ? '长期投放' : `${begin_date} ~ ${end_date}`
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                is_render_edit && h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        s.form_data_config.schedule_date.schedule_type.on = {
                          change(val) {
                            s.form_data_config.schedule_date.delivery_date.hidden = isNow(val);
                          },
                        };
                        s.form_data_config.schedule_date.schedule_type.value = schedule_type;
                        s.form_data_config.schedule_date.schedule_type.on.change(s.form_data_config.schedule_date.schedule_type.value);
                        const has_begin_date = isString(begin_date) && !isEmpty(begin_date);
                        const has_end_date = isString(end_date) && !isEmpty(end_date);
                        const filed_val = (has_begin_date && has_end_date) ? [begin_date, end_date] : [(() => getDate())(), (() => getDate(7))()];
                        s.form_data_config.schedule_date.delivery_date.value = filed_val;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.schedule_date,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        const { schedule_type, delivery_date } = data;
                        campaign.schedule_type = schedule_type;
                        if (isArray(delivery_date)) {
                          Object.assign(campaign, {
                            begin_date: delivery_date[0],
                            end_date: delivery_date[1],
                          });
                        } else {
                          delete campaign.begin_date;
                          delete campaign.end_date;
                        }
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '广告预算',
      align: 'center',
      width: 200,
      value: 'daily_budget',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { property } = column;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = !s.isAddCreative;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate inline-block',
              },
              s.getDailyBudgetCellValue({ row })
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                is_render_edit && h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        s.$set(s.form_data_config, 'daily_budget', s.getDailyBudgetFields());
                        s.setDailyBudgetFiledProps({ row, form_data: s.form_data_config.daily_budget });
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.daily_budget,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        s.confirmDailyBudgetForm({ row, data });
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '转化事件',
      align: 'center',
      width: 200,
      value: 'convert_id',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { property } = column;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const deep_type_enum_val_key = 'deep_type_enum_val_key';
        const deep_bid_type_enum_val_key = 'deep_bid_type_enum_val_key';
        const deep_type_options_val_key = 'deep_type_options_val_key';
        /** 是否渲染转化事件编辑按钮 */
        const is_render_edit = s.supportEditConvertEvent(row) && !s.isAddCreative;

        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              s.getConvertCellValue({ row, convert_goal_enum_val_key, deep_type_enum_val_key, deep_bid_type_enum_val_key })
            ),
            is_render_edit && h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        s.$set(s.form_data_config, 'convert_id', s.getConvertFields());
                        s.renderConvertEventForm({
                          row,
                          convert_goal_enum_val_key,
                          deep_type_enum_val_key,
                          deep_bid_type_enum_val_key,
                          deep_type_options_val_key,
                          form_data: s.form_data_config.convert_id,
                        });
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    scopedSlots: {
                      custom: ({ form_key }) => {
                        return h('div', null, [
                          form_key === 'convert_id' && h('div',
                            {
                              class: 'flex items-center',
                            },
                            [
                              h('el-button',
                                {
                                  props: {
                                    size: 'mini',
                                    type: 'primary',
                                  },
                                  on: {
                                    click() {
                                      s.onDataDialog({
                                        title: '转化事件目标',
                                        form_key,
                                        payload: {
                                          row,
                                          form_data: s.form_data_config.convert_id,
                                        },
                                      });
                                    },
                                  },
                                }, '选择转化目标'),
                            ]),
                          /** 变现ROI目标 */
                          form_key === 'monetization_converts' && h('div',
                            {
                              class: 'flex items-center',
                              style: {
                                width: '120px',
                              },
                            },
                            [
                              h('el-button',
                                {
                                  props: {
                                    size: 'mini',
                                    type: 'primary',
                                  },
                                  on: {
                                    click() {
                                      s.onDataDialog({
                                        title: '转化事件-广点通',
                                        form_key,
                                        payload: {
                                          row,
                                          form_data: s.form_data_config.convert_id,
                                        },
                                      });
                                    },
                                  },
                                }, '选择转化事件'),
                            ]),
                          /** 付费ROI目标 */
                          form_key === 'purchase_converts' && h('div',
                            {
                              class: 'flex items-center',
                              style: {
                                width: '120px',
                              },
                            },
                            [
                              h('el-button',
                                {
                                  props: {
                                    size: 'mini',
                                    type: 'primary',
                                  },
                                  on: {
                                    click() {
                                      s.onDataDialog({
                                        title: '转化事件-广点通',
                                        form_key,
                                        payload: {
                                          row,
                                          form_data: s.form_data_config.convert_id,
                                        },
                                      });
                                    },
                                  },
                                }, '选择转化事件'),
                            ]),
                        ]);
                      },
                    },
                    props: {
                      formData: s.form_data_config.convert_id,
                      defaultLabelWidth: '120px',
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        row[deep_type_enum_val_key] = transformSchema(s.form_data_config.convert_id.deep_convert_type.options || []);
                        row[deep_type_options_val_key] = s.form_data_config.convert_id.deep_convert_type.options || [];
                        s.confirmConvertEventForm({ row, data, convert_goal_enum_val_key, deep_type_options_val_key, deep_bid_type_enum_val_key });
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '广告出价',
      align: 'center',
      width: 200,
      value: 'price',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { property } = column;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const deep_type_enum_val_key = 'deep_type_enum_val_key';
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = s.supportEditPrice(row) && !s.isAddCreative;
        const ele = h('div', { class: 'flex justify-center items-center' }, [
          h(
            'div',
            {
              class: 'ml-10 mr-10 min-w-0 truncate inline-block',
            },
            s.getPriceCellValue({ row, convert_goal_enum_val_key })
          ),
          is_render_edit && h(
            'el-popover',
            {
              ref: popover_ref_key,
              props: {
                trigger: 'manual',
              },
            },
            [
              h(
                'i',
                {
                  slot: 'reference',
                  class: 'el-icon-edit-outline font-16',
                  on: {
                    click() {
                      s.closeAllTablePopover();
                      s.$set(s.form_data_config, 'price', s.getPriceFields());
                      s.setPriceFiledProps({ row, form_data: s.form_data_config.price, convert_goal_enum_val_key, deep_type_enum_val_key });
                      showPopover(s, popover_ref_key);
                    },
                  },
                },
                null
              ),
              h(
                'app-form',
                {
                  props: {
                    formData: s.form_data_config.price,
                    defaultLabelWidth: '150px',
                  },
                  on: {
                    cancel() {
                      closePopover(s, popover_ref_key);
                    },
                    confirm(data) {
                      s.confirmPriceForm({ row, data });
                      closePopover(s, popover_ref_key);
                    },
                  },
                },
                null
              ),
            ]
          ),
        ]);
        return ele;
      },
    },
    {
      name: '广告定向',
      align: 'center',
      width: 200,
      value: 'targeting_id',
      format: (row, column, _, row_index) => {
        const { campaign, advertiser_id } = row;
        const { targeting_id, targeting_name } = campaign;
        const h = s.$createElement;
        const { property } = column;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = !s.isAddCreative;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate inline-block',
              },
              targeting_name
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                is_render_edit && h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        const raw_options = s.getAssignList('targeting_config');
                        const options = raw_options.filter(option => String(option.value.advertiser_id) === String(advertiser_id));
                        const option = options.find(option => String(option.value.id) === String(targeting_id));
                        s.form_data_config.targeting_id.targeting_id.options = options;
                        s.form_data_config.targeting_id.targeting_id.value = option.value;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.targeting_id,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm(data) {
                        campaign.targeting_id = data.targeting_id.id;
                        campaign.targeting_name = data.targeting_id.name;
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '广告操作',
      align: 'center',
      width: 200,
      format: ({ custom_marker: { coordinate = {}} = {}, advertiser_id }) => {
        const campaigns = s.$parent?.getAdvertiserCampaigns(advertiser_id) || [];
        const is_disabled = campaigns.length <= 1;
        const h = s.$createElement;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'el-button',
              {
                props: {
                  size: 'mini',
                  disabled: is_disabled,
                },
                on: {
                  click: () => {
                    s.$emit('remove', {
                      target: 'campaign',
                      payload: {
                        coordinate,
                      },
                    });
                  },
                },
              },
              '移除'
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '创意名称',
      align: 'center',
      width: 200,
      value: 'creative_name',
      format: (row, column, row_index) => {
        const h = s.$createElement;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property: column.property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const { creative } = row;
        const { creative_name } = creative;
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              creative_name
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        s.form_data_config.creative_name.creative_name.value = creative_name;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.creative_name,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm({ creative_name }) {
                        Object.assign(creative, { creative_name });
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),
          ]
        );
        return ele;
      },
    },
    {
      name: '创意出价',
      align: 'center',
      width: 200,
      value: 'creative_price',
      format: (row, column, _, row_index) => {
        const h = s.$createElement;
        const { property } = column;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const is_render_edit = s.supportEditCreativePrice(row);
        const ele = h('div', { class: 'flex justify-center items-center' }, [
          h(
            'div',
            {
              class: 'ml-10 mr-10 min-w-0 truncate inline-block',
            },
            s.getCreativePriceCellValue({ row, convert_goal_enum_val_key })
          ),
          is_render_edit && h(
            'el-popover',
            {
              ref: popover_ref_key,
              props: {
                trigger: 'manual',
              },
            },
            [
              h(
                'i',
                {
                  slot: 'reference',
                  class: 'el-icon-edit-outline font-16',
                  on: {
                    click() {
                      s.closeAllTablePopover();
                      s.$set(s.form_data_config, 'creative_price', s.getCreativePriceFields());
                      s.setCreativePriceFiledProps({ row, form_data: s.form_data_config.creative_price });
                      showPopover(s, popover_ref_key);
                    },
                  },
                },
                null
              ),
              h(
                'app-form',
                {
                  props: {
                    formData: s.form_data_config.creative_price,
                    defaultLabelWidth: '150px',
                  },
                  on: {
                    cancel() {
                      closePopover(s, popover_ref_key);
                    },
                    confirm(data) {
                      s.confirmCreativePriceForm({ row, data });
                      closePopover(s, popover_ref_key);
                    },
                  },
                },
                null
              ),
            ]
          ),
        ]);
        return ele;
      },
    },
    {
      name: '落地页',
      align: 'center',
      width: 200,
      value: 'sites',
      format: (row, column, row_index) => {
        const h = s.$createElement;
        const base_popover_options = {
          row_index,
          column_index: column.index,
          property: column.property,
        };
        const popover_ref_key = getPopoverRefKey(base_popover_options);
        const { campaign, creative, advertiser_id } = row;
        const { sites = [] } = creative;
        const sites_name = sites.map(({ name }) => name).join('、');
        const ele = h(
          'div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate inline-block',
              },
              sites_name
            ),
            h(
              'el-popover',
              {
                ref: popover_ref_key,
                props: {
                  trigger: 'manual',
                },
              },
              [
                h(
                  'i',
                  {
                    slot: 'reference',
                    class: 'el-icon-edit-outline font-16',
                    on: {
                      click() {
                        s.closeAllTablePopover();
                        const options = s.getAssignList('site_config');
                        s.form_data_config.sites.sites.options = options.filter(({ value }) => String(value.advertiser_id) === String(advertiser_id));
                        s.form_data_config.sites.sites.value = sites;
                        showPopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
                h(
                  'app-form',
                  {
                    props: {
                      formData: s.form_data_config.sites,
                    },
                    on: {
                      cancel() {
                        closePopover(s, popover_ref_key);
                      },
                      confirm({ sites }) {
                        Object.assign(creative, { sites });
                        closePopover(s, popover_ref_key);
                      },
                    },
                  },
                  null
                ),
              ]
            ),

          ]
        );
        /**
         * 如果是小米渠道，并且创意形式为以下类型时，则固定展示文本“应用详情页”
         * - 大图：1
         * - 小图：2
         * - 组图：3
         * - 竖图：4
         * - 横版视频：5
         * - 竖版视频：6
         * - 开屏图片：7(额外条件：计划类型为'商店'(campaign_type: 1))
         * 其他创意形式类型下以'-'占位
         * 只要是小米渠道，落地页不可编辑
         */
        const renderCellValue = () => {
          if (s.init_ops.channel_interface === CHANNEL_INTERFACE_TYPE.XIAOMI) {
            const { campaign_type } = campaign;
            const { creative_template_id } = creative;
            const is_match = [1, 2, 3, 4, 5, 6].some((value) => creative_template_id === value);
            const is_open_screen = creative_template_id === 7;
            const is_store = campaign_type === 1;
            const is_open_screen_store = is_open_screen && is_store;
            return (is_match || is_open_screen_store) ? '应用详情页' : '-';
          } else {
            return sites?.length ? ele : '-';
          }
        };
        return renderCellValue();
      },
    },
    {
      name: '标题',
      align: 'center',
      width: 200,
      format: (row, column, row_index) => {
        const h = s.$createElement;
        const { creative } = row;
        const { titles = [] } = creative;
        const titles_list = titles.map(name => ({ name }));
        /** 类型是标题组件时使用下拉框的形式 */
        if (s.getTitleComponentType() === 'component') {
          const base_popover_options = {
            row_index,
            column_index: column.index,
            property: column.property,
          };
          const popover_ref_key = getPopoverRefKey(base_popover_options);
          const titles_name = titles_list.map(({ name }) => name).join('、');
          const ele = h(
            'div',
            {
              class: 'flex justify-center items-center',
            },
            [
              h(
                'div',
                {
                  class: 'ml-10 mr-10 min-w-0 truncate inline-block',
                },
                titles_name
              ),
              h(
                'el-popover',
                {
                  ref: popover_ref_key,
                  props: {
                    trigger: 'manual',
                  },
                },
                [
                  h(
                    'i',
                    {
                      slot: 'reference',
                      class: 'el-icon-edit-outline font-16',
                      on: {
                        click() {
                          s.closeAllTablePopover();
                          s.form_data_config.titles.titles.options = s.getAssignList('title_config');
                          /** 分配的标题与分配的数据项完全匹配才设置为value */
                          s.form_data_config.titles.titles.value = s.form_data_config.titles.titles.options.filter(({ value }) => titles_list.some(title => title.name === value.name)).map(({ value }) => value);
                          Object.assign(s.form_data_config.titles.titles.props, { multipleLimit: s.getTitlesLimitNum() });
                          showPopover(s, popover_ref_key);
                        },
                      },
                    },
                    null
                  ),
                  h(
                    'app-form',
                    {
                      props: {
                        formData: s.form_data_config.titles,
                      },
                      on: {
                        cancel() {
                          closePopover(s, popover_ref_key);
                        },
                        confirm({ titles }) {
                          Object.assign(creative, {
                            titles: titles.map(({ name }) => name),
                          });
                          closePopover(s, popover_ref_key);
                        },
                      },
                    },
                    null
                  ),
                ]
              ),
            ]
          );
          return titles?.length ? ele : '-';
        }
        const ele = h(
          'title-view-form',
          {
            props: {
              showDelBtn: false,
              showEditButton: true,
              height: 'auto',
              formFields: {
                selection_config: {
                  label_key: 'name',
                  list: titles_list,
                },
              },
            },
            on: {
              change: () => {
                Object.assign(creative, { titles: titles_list.map(({ name }) => name) });
              },
            },
          },
          null
        );
        return titles?.length ? ele : '-';
      },
    },
    {
      name: '图片/视频素材',
      align: 'center',
      width: 200,
      format: (row) => {
        const h = s.$createElement;
        const { creative } = row;
        const { images = [], videos = [] } = creative;
        const preview_images = images.map(({ id, name, urls }) => {
          return {
            id,
            name,
            material_type: urls.length > 1 ? 4 : 1,
            url: urls.map(url => ({ url, thumb_url: url })),
          };
        });
        const preview_videos = videos.map(({ id, name, url, cover, horizontal_cover, width, height }) => {
          return {
            id,
            name,
            material_type: 2,
            url: [{ url, thumb_url: cover, ...(horizontal_cover && { horizontal_cover }) }],
            width,
            height,
          };
        });
        const preview_materials = [...preview_images, ...preview_videos];
        const ele = h('div',
          { class: 'flex justify-center items-center' },
          [
            h(
              'div',
              {
                class: 'ml-10 mr-10 min-w-0 truncate',
              },
              preview_materials?.length ? [
                h('material-preview', {
                  props: {
                    materialType: preview_materials[0].material_type,
                    materialList: preview_materials[0].url,
                  },
                }, null),
              ] : '-'
            ),
            h('span', null, preview_materials?.length),
            h(
              'i',
              {
                class: 'el-icon-edit-outline font-16',
                on: {
                  click: () => {
                    s.onDataDialog({
                      title: '编辑素材配置',
                      form_key: 'materials',
                      payload: {
                        row,
                        materials: preview_materials,
                      },
                    });
                  },
                },
              },
              null
            ),
          ]);
        return preview_materials?.length ? ele : '-';
      },
    },
    {
      name: '创意组件',
      align: 'center',
      width: 200,
      value: 'creative_components',
      format: (row) => {
        const h = s.$createElement;
        const { channel_interface } = s.init_ops;
        const is_render_edit = s.supportEditCreativeComponents(row);
        const ele = h('div', { class: 'flex justify-center items-center' }, [
          h(
            'div',
            {
              class: 'ml-10 mr-10 min-w-0 truncate inline-block',
            },
            s.getCreativeComponentsCellValue({ row })
          ),
          is_render_edit && h(
            'i',
            {
              slot: 'reference',
              class: 'el-icon-edit-outline font-16',
              on: {
                click() {
                  s.closeAllTablePopover();
                  s.$set(s.edit_dialog_config, 'form_data', s.getCreativeComponentsFields());
                  const strategy = {
                    [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
                      return {
                        star_task_author_videos_filter_config_key: 'star_task_author_videos_filter_config_key',
                      };
                    },
                    [CHANNEL_INTERFACE_TYPE.GDT]: () => {
                      return {
                        wxgame_playable_page_path_key: 'wxgame_playable_page_path_key',
                      };
                    },
                    default: () => {},
                  };
                  const custom_config = (strategy[channel_interface] || strategy['default'])();
                  s.setCreativeComponentsFiledProps({ row, form_data: s.edit_dialog_config.form_data, custom_config });
                  s.openEditDialog({ row, parent_key: 'creative_config', children_key: 'creative_components', custom_config });
                },
              },
            },
            null
          ),
        ]);
        return ele;
      },
    },
    {
      name: '创意操作',
      align: 'center',
      width: 200,

      format: ({ custom_marker: { coordinate = {}} = {}, advertiser_id }) => {
        const h = s.$createElement;
        const campaigns = s.$parent?.getAdvertiserCampaigns(advertiser_id) || [];
        const { campaign: campaign_index } = coordinate;
        const is_render_remove_btn = (campaigns[campaign_index]?.creatives || []).length > 1;
        const ele = h('div',
          {
            class: 'flex justify-center items-center',
          },
          [
            h(
              'el-button',
              {
                props: {
                  type: 'success',
                  size: 'mini',
                },
                on: {
                  click: () => {
                    s.$emit('copy', {
                      target: 'creative',
                      payload: {
                        coordinate,
                      },
                    });
                  },
                },
              },
              '复制创意'
            ),
            is_render_remove_btn && h(
              'el-button',
              {
                props: {
                  size: 'mini',
                },
                on: {
                  click: () => {
                    s.$emit('remove', {
                      target: 'creative',
                      payload: {
                        coordinate,
                      },
                    });
                  },
                },
              },
              '移除'
            ),
          ]);
        return ele;
      },
    },
  ];
};
